import { DateTime } from "luxon";
import { concat, Observable, of, switchMap, zip } from "rxjs";
import { Attachment } from "./attachment.service";
import { CommentResponse, WorkItemComment, CommentEdited } from "./comment.service";
import { Incident } from "./incidents.service";

export type DateFieldTypes = "utc" | "central" | "local"

export interface DateField<T> {
    fieldName: keyof T,
    dateType: DateFieldTypes
}

export type NullFieldValues<T> = { [K in keyof T]: {
    fieldName: K,
    value: T[K]
} }[keyof T]

export class DataTransform {

    /**
     * @deprecated prefer the generic replaceDatesArray
     * @param incidents 
     * @returns 
     */
    static replaceIncidentDatesArray(incidents: Incident[]) {
        for (const incident of incidents) {
            if (typeof incident.dateCreated === 'string') {
                incident.dateCreated = DateTime.fromISO(incident.dateCreated as unknown as string, { setZone: true }).toJSDate();
            }

            if (typeof incident.startDate === 'string') {
                incident.startDate = DateTime.fromISO(incident.startDate as unknown as string, { setZone: true }).setZone('local', { keepLocalTime: true }).toJSDate();
            }
        }
        return of(incidents);
    }


    /**
     * @deprecated Prefer the generic replaceDates
     * @param incident 
     * @returns 
     */
    static replaceIncidentDates(incident: Incident) {

        if (incident) {
            if (typeof incident.dateCreated === 'string') {
                incident.dateCreated = DateTime.fromISO(incident.dateCreated as unknown as string, { setZone: true }).toJSDate();
            }
            if (typeof incident.startDate === 'string') {
                incident.startDate = DateTime.fromISO(incident.startDate as unknown as string, { setZone: true }).setZone('local').toJSDate();
            }
        }
        return of(incident);
    }

    static attachStaffToComment(response: CommentResponse) {
        for (const comment of response.comments) {
            comment.staff = response.staff.find(staff => staff.id === comment.staffId);
        }
        return of(response);
    }

    /**
     * @deprecated Prefer the generic replaceDatesArray
     * @param response 
     * @returns 
     */
    static replaceCommentDatesArray(response: CommentResponse) {
        for (const comment of response.comments) {
            if (typeof comment.commentDate === 'string') {
                comment.commentDate = DateTime.fromISO(comment.commentDate).toJSDate();
            }
        }
        return of(response);
    }
    /**
     * @deprecated Prefer the generic replaceDatesArray
     * @param attachments 
     * @returns 
     */
    static replaceAttachmentDatesArray(attachments: Attachment[]) {
        for (const attachment of attachments) {
            if (typeof attachment.dateAttached === 'string') {
                attachment.dateAttached = DateTime.fromISO(attachment.dateAttached).toJSDate();
            }
        }
        return of(attachments);
    }

    static replaceDates<T extends { [key: string]: any }>(item: T, dateFields: Array<DateField<T>>): Observable<T> {

        return of(this.replaceDatesDirect(item, dateFields));
    }

    static replaceDatesDirect<T extends { [key: string]: any }>(item: T, dateFields: Array<DateField<T>>): T {
        for (const field of dateFields) {

            if (Object.prototype.hasOwnProperty.call(item, field.fieldName)) {



                if (typeof item[field.fieldName] === "string" && item[field.fieldName] !== null && item[field.fieldName] !== undefined) {

                    switch (field.dateType) {
                        case "central":
                            (item[field.fieldName] as unknown as Date) = DataTransform.parseDate(item[field.fieldName] as unknown as string, true)
                                .setZone('America/Chicago', { keepLocalTime: true }).toJSDate();
                            break;

                        case "local":
                            (item[field.fieldName] as unknown as Date) = DataTransform.parseDate(item[field.fieldName] as unknown as string, true)
                                .setZone('local', { keepLocalTime: true }).toJSDate();
                            break;
                        case "utc":
                            (item[field.fieldName] as unknown as Date) = DataTransform.parseDate(item[field.fieldName] as unknown as string, false)
                                .setZone('utc', { keepLocalTime: true }).toJSDate();
                            break;
                    }


                }

            }

        }

        return item;
    }

    static parseDate(date: string, setZone: boolean = true) {
        let returnDate: DateTime = DateTime.fromISO(date, { setZone: setZone })

        if (!returnDate.isValid) returnDate = DateTime.fromSQL(date, { setZone: setZone })

        return returnDate
    }

    static replaceDatesForUpdate<T extends { [key: string]: any }>(item: T, dateFields: Array<DateField<T>>): T {
        const localItem: T = { ...item }
        for (const field of dateFields) {
            if (Object.prototype.hasOwnProperty.call(localItem, field.fieldName)) {
                switch (field.dateType) {
                    case "local": //we send the local time as UTC for fields that are logically parsed as a local time, such as tripdate
                        (localItem[field.fieldName] as Date) = DateTime.fromJSDate(localItem[field.fieldName]).setZone("utc", { keepLocalTime: true }).toJSDate()
                        break;
                    case "central": //convert to central, then convert to UTC, keeping the time the same number (not timestamp) because the backend data is stored in central time
                        (localItem[field.fieldName] as Date) = DateTime.fromJSDate(localItem[field.fieldName]).setZone("America/Chicago").setZone("utc", { keepLocalTime: true }).toJSDate()
                        break;
                    case "utc":
                    //nothing to do.  It'll happen automatically

                }
            }
        }

        return localItem
    }



    static replaceDatesArray<T extends { [key: string]: any }>(arr: T[], dateFields: Array<DateField<T>>): Observable<T[]> {

        return of(this.replaceDatesArrayDirect(arr, dateFields))

    }

    static replaceDatesArrayDirect<T extends { [key: string]: any }>(arr: T[], dateFields: Array<DateField<T>>): T[] {
        let results: T[] = []
        for (const item of arr) {
            results = [...results, this.replaceDatesDirect(item, dateFields)]
        }
        return results

    }

    /**
     * Replace json string fields on an object with the actual javascript object representation
     * @param item The object containing the json field
     * @param jsonFields Array containing json field names to transform
     * @returns 
     */
    static replaceJson<T extends { [key: string]: any }>(item: T, jsonFields: Array<keyof T>): Observable<T> {
        for (const field of jsonFields) {

            if (Object.prototype.hasOwnProperty.call(item, field)) {
                if (typeof item[field] === "string" && item[field] !== null && item[field] !== undefined) {
                    item[field] = JSON.parse(item[field])
                }
            }

        }

        return of(item)
    }


    /**
     * Replaces json string fields on the provided objects with the actual javascript object representation
     * @param arr The array of objects containing json fields to replace
     * @param jsonFields Array containing the field names to transform
     * @returns 
     */
    static replaceJsonArray<T extends { [key: string]: any }>(arr: T[], jsonFields: Array<keyof T>): Observable<T[]> {
        let observables: Observable<T>[] = []
        for (const item of arr) {
            observables = [...observables, this.replaceJson(item, jsonFields)]
        }

        if (observables.length === 0) {
            return of([])
        }
        else {
            return zip(observables)
        }
    }

    /**
     * Replaces null values for the specified fields with the corresponding value
     * @param item The item to perform replacements on
     * @param nullFieldValues Array of fields with null replacement values
     */
    static replaceNullValues<T extends { [key: string]: any }>(item: T, nullFieldValues: NullFieldValues<T>[]) {

        for (const field of nullFieldValues) {
            if (Object.prototype.hasOwnProperty.call(item, field.fieldName)) {
                if (item[field.fieldName] === null) {
                    item[field.fieldName] = field.value
                }
            }
        }

        return of(item)
    }

    static replaceNullValuesArray<T extends { [key: string]: any }>(arr: T[], nullFieldValues: NullFieldValues<T>[]) {
        let observables: Observable<T>[] = []
        for (const item of arr) {
            observables = [...observables, this.replaceNullValues(item, nullFieldValues)]
        }

        if (observables.length === 0) {
            return of([])
        }
        else {
            return zip(observables)
        }
    }


}